<template>
  <div class="modelling">
    <modelling-left-panel>
      <modelling-model-list :models="models" @change-model="changeModel" />
      <div class="modelling-dock" v-show="modelId">
        <div class="modelling-dock-title">
          Доступные элементы моделирования
        </div>
        <div class="modelling-dock-container" ref="dock" />
      </div>
    </modelling-left-panel>
    <div class="modelling-layout">
      <div class="modelling-editor" ref="editor" />
      <modelling-controls
        :saving="saving"
        @start-calc="startCalc"
        v-show="modelId"
      />
      <modelling-log v-show="modelId" />
    </div>
  </div>
</template>

<script>
import ModellingControls from "./components/modelling-controls";
import ModellingLeftPanel from "./components/modelling-left-panel";
import ModellingLog from "./components/modelling-log";
import ModellingModelList from "./components/modelling-model-list";
import init from "./core";
import { getDatasourcesByDatatype, notifyFactory } from "@/utils";

const modellingDatatypes = ["sta_results", "skim_matrices", "demand_matrices"];
const calculations = ["SkimMatrix", "DemandMatrix", "STA"];
const calculationsAttrs = {
  DemandMatrix: ["a", "b", "c", "utility_func", "balancing"],
  STA: ["start", "step", "horizone"]
};
const errorsMessages = {
  "input-data": "Необходимо указать все входящие атрибуты",
  "no-data": "Необходимо заполнить все атрибуты блоков",
  "output-data": "Необходимо указать все выходящие атрибуты",
  calcs: "Необходимо указать цепочку расчетов"
};

export default {
  data() {
    return {
      editor: null,
      engine: null,
      models: [],
      modelId: "",
      modelIds: {
        sta_results: "",
        skim_matrices: "",
        demand_matrices: ""
      },
      schema: null,
      saving: false
    };
  },
  components: {
    ModellingControls,
    ModellingLeftPanel,
    ModellingModelList,
    ModellingLog
  },
  methods: {
    async changeModel({ id, model }) {
      const { children, calculation_schema } = model;
      const filtered = children
        .filter(({ datatype }) => modellingDatatypes.indexOf(datatype) > -1)
        .map(({ datatype, id }) => ({ datatype, id }));

      filtered.forEach(({ datatype, id }) => (this.modelIds[datatype] = id));

      this.modelId = id;
      this.schema = calculation_schema ? calculation_schema : null;

      await this.init();
    },
    async startCalc() {
      const calculation_schema = await this.editor.toJSON();

      try {
        this.saving = true;

        const calcSchema = this.getCalcSchema(calculation_schema);
        const data = {
          id: this.modelId,
          calculation_schema
        };
        const url = `permission_object/${this.modelId}`;
        await this.$store.dispatch("PUT_REQUEST", {
          data,
          url
        });
        await this.getModels();

        const calcUrl = `worker/CalculationRuner`;
        const calcData = new FormData();
        calcData.append("source_id", this.modelId);
        calcData.append("calculation_schema", JSON.stringify(calcSchema));

        await this.$store.dispatch("SEND_FORM_DATA", {
          url: calcUrl,
          data: calcData
        });

        const message = "Запуск расчета по указанной схеме выполнен успешно.";
        this.$notify(
          notifyFactory("success", "Запуск расчета выполнен", message)
        );

        this.saving = false;
      } catch (error) {
        this.saving = false;
        const message = errorsMessages[error.message];
        this.$notify(
          notifyFactory("error", "Невозможно выполнить расчет", message)
        );
        console.log(error);
      }
    },
    getCalcSchema(schema) {
      const { nodes } = schema;
      const calcNodes = Object.values(nodes).filter(
        ({ name }) => calculations.indexOf(name) > -1
      );

      const startNodes = calcNodes.filter(
        node => !node.inputs["calculation-input"].connections.length
      );
      if (startNodes.length > 1) throw new Error("calcs");
      const startNode = startNodes[0];
      const nodesOrder = [];
      const getNodeOrder = node => {
        const { id, outputs } = node;
        nodesOrder.push(id);
        const outputConnections = outputs["calculation-output"].connections;
        if (outputConnections.length) {
          const nodeId = outputConnections[0].node;
          if (nodes[nodeId]) getNodeOrder(nodes[nodeId]);
        }
      };
      getNodeOrder(startNode);

      const schemeNodes = nodesOrder
        .map(id => nodes[id])
        .map((node, i) => {
          const { id, name, inputs, data } = node;
          const resultNode = {
            id,
            name,
            input: {},
            order: i + 1
          };

          switch (name) {
            case "SkimMatrix": {
              // skim input
              if (!data.id) throw new Error("no-data");
              resultNode.input.skim_matrix_id = data.id;
              resultNode.active = data.switch;
              break;
            }
            case "DemandMatrix": {
              // demand output
              if (!data.demandId) throw new Error("no-data");
              resultNode.input.demand_matrix_id = data.demandId;
              resultNode.active = data.switch;
              // skim input
              const skimConnections = inputs["skim-input"].connections;
              if (!skimConnections.length) throw new Error("input-data");
              const skimNodeId = skimConnections[0].node;
              const skimMatrixId = nodes[skimNodeId].data.id;
              if (!skimMatrixId) throw new Error("input-data");
              resultNode.input.skim_matrix_id = skimMatrixId;
              // control data
              calculationsAttrs[name].forEach(attr => {
                if (!data[attr]) throw new Error("no-data");
                resultNode.input[attr] = data[attr];
              });
              break;
            }
            case "STA": {
              // sta output
              if (!data.staId) throw new Error("no-data");
              resultNode.input.sta_result_id = data.staId;
              resultNode.active = data.switch;
              // demand input
              const demandConnections = inputs["demand-input"].connections;
              if (!demandConnections.length) throw new Error("input-data");
              const demandNodeId = demandConnections[0].node;
              const demandMatrixId = nodes[demandNodeId].data.demandId;
              if (!demandMatrixId) throw new Error("input-data");
              resultNode.input.demand_matrix_id = demandMatrixId;
              // control data
              calculationsAttrs[name].forEach(attr => {
                if (!data[attr]) throw new Error("no-data");
                resultNode.input[attr] = data[attr];
              });
              break;
            }
          }
          return resultNode;
        });

      const resultNodes = schemeNodes.filter(e => e.active);

      if (!Object.keys(resultNodes).length) throw new Error("calcs");

      return resultNodes;
    },
    async init() {
      this.$refs.editor.innerHTML = "";
      this.$refs.dock.innerHTML = "";

      if (this.editor) this.editor.destroy();
      if (this.engine) this.engine.destroy();

      const { editor, engine } = await init(
        this.$refs.editor,
        this.$refs.dock,
        this
      );
      this.editor = editor;
      this.engine = engine;
      if (!this.modelId) {
        this.editor.on("showcontextmenu", () => false);
      } else {
        this.editor.on("showcontextmenu", () => true);
      }

      if (this.schema) this.editor.fromJSON(this.schema);
    },
    async getModels() {
      const where = {
        field: "datatype",
        op: "=",
        value: "model",
        type: "AND"
      };
      const include = { children: {} };
      const models = await getDatasourcesByDatatype(this, where, include);

      this.models = models.filter(
        ds =>
          ds.children.filter(
            l =>
              l.datatype === "count_points" ||
              l.datatype === "general_count_points"
          ).length
      );
    }
  },
  created() {
    this.getModels();
  },
  mounted() {
    this.init();
  }
};
</script>

<style lang="scss" src="./modelling.scss"></style>
